import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar } from "@mui/material";
import { Alert } from "@material-ui/lab";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import DemandeAchatService from "../../services/DemandeAchatService";
import api from "../../services/api";
import UtilsUrl from "../../utils/UtilsUrl";

const SERVICES = [
  "Finance",
  "Direction",
  "Ressources Humaines",
  "Informatique",
  "Logistique",
  "Commercial",
  "Production",
  "Marketing",
  "Autre",
];

const AddDemandeAchat = () => {
  const navigate = useNavigate();

  const [userRole, setUserRole] = useState([]);
  const [demandeur, setDemandeur] = useState("");
  const [employes, setEmployes] = useState([]);
  const [service, setService] = useState("");
  const [note, setNote] = useState("");
  const [produits, setProduits] = useState([{ designation: "", quantite: 1 }]);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorAlertText, setErrorAlertText] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const status = localStorage.getItem("status");
    const roles = status ? JSON.parse(status) : [];
    setUserRole(roles);

    const isAdmin = roles.includes("ADMIN");

    if (isAdmin) {
      api
        .get(UtilsUrl.rhURL() + "/employees/" + getCurrentUserId())
        .then((response) => {
          if (Array.isArray(response.data)) {
            setEmployes(response.data);
          } else if (response.data?.success) {
            setEmployes(response.data.success);
          }
        })
        .catch(() => {});
    } else {
      const user = localStorage.getItem("user");
      if (user) {
        try {
          const parsed = JSON.parse(user);
          const firstName = parsed?.token?.firstName || "";
          const lastName = parsed?.token?.lastName || "";
          setDemandeur((firstName + " " + lastName).trim());
        } catch (_) {}
      }
    }
  }, []);

  const isAdmin = userRole.includes("ADMIN");

  const handleAddProduit = () => {
    setProduits([...produits, { designation: "", quantite: 1 }]);
  };

  const handleRemoveProduit = (index) => {
    setProduits(produits.filter((_, i) => i !== index));
  };

  const handleProduitChange = (index, field, value) => {
    const updated = [...produits];
    updated[index][field] = field === "quantite" ? parseInt(value) || 1 : value;
    setProduits(updated);
  };

  const handleSubmit = () => {
    if (!demandeur.trim()) {
      setErrorAlertText("Erreur : le demandeur est obligatoire");
      setOpenErrorAlert(true);
      return;
    }
    if (!service) {
      setErrorAlertText("Erreur : le service est obligatoire");
      setOpenErrorAlert(true);
      return;
    }
    if (produits.some((p) => !p.designation.trim())) {
      setErrorAlertText("Erreur : tous les produits doivent avoir une désignation");
      setOpenErrorAlert(true);
      return;
    }

    setLoading(true);
    const payload = {
      idUser: getCurrentUserId(),
      demandeur,
      service,
      note,
      listeProduits: produits,
    };

    DemandeAchatService.createDemandeAchat(payload)
      .then((response) => {
        setLoading(false);
        if (Object.keys(response.data)[0] === "success") {
          setOpenSuccessAlert(true);
          setTimeout(() => navigate("/demandesAchat"), 1500);
        } else {
          setErrorAlertText("Erreur lors de la création de la demande");
          setOpenErrorAlert(true);
        }
      })
      .catch(() => {
        setLoading(false);
        setErrorAlertText("Erreur de connexion au serveur");
        setOpenErrorAlert(true);
      });
  };

  const labelStyle = {
    fontSize: "0.8rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#6c757d",
  };
  const cardStyle = { borderRadius: 8, boxShadow: "0 1px 6px rgba(0,0,0,.08)" };
  const required = <span style={{ color: "#dc3545" }}>*</span>;
  const totalQuantite = produits.reduce((sum, p) => sum + (p.quantite || 0), 0);

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <i className="fas fa-file-invoice mr-2"></i>Nouvelle demande
                d'achat
              </h4>
              <small className="text-muted">
                Renseignez le demandeur, le service et les produits souhaités
              </small>
            </div>
            <button
              className="btn btn-outline-secondary mt-2 mt-sm-0"
              onClick={() => navigate("/demandesAchat")}
            >
              <i className="fas fa-arrow-left mr-1"></i>
              Retour aux demandes
            </button>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Informations */}
          <div className="card card-outline card-dark mt-3" style={cardStyle}>
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-info-circle mr-2"></i>Informations
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label style={labelStyle}>Demandeur {required}</label>
                    {isAdmin ? (
                      <select
                        className="form-control"
                        value={demandeur}
                        onChange={(e) => setDemandeur(e.target.value)}
                      >
                        <option value="">-- Sélectionner un employé --</option>
                        {employes.map((emp, i) => {
                          const fullName = (
                            (emp.firstName || emp.firstname || emp.prenom || "") +
                            " " +
                            (emp.lastName || emp.lastname || emp.nom || "")
                          ).trim();
                          return (
                            <option key={emp.id || i} value={fullName}>
                              {fullName}
                            </option>
                          );
                        })}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={demandeur}
                        disabled
                      />
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label style={labelStyle}>Service {required}</label>
                    <select
                      className="form-control"
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                    >
                      <option value="">-- Sélectionner un service --</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group mb-0">
                <label style={labelStyle}>Note / Justification</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Justification ou contexte de la demande..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Produits */}
          <div className="card card-outline card-dark" style={cardStyle}>
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="card-title mb-0">
                  <i className="fas fa-boxes mr-2"></i>Produits demandés {required}
                  <span className="badge badge-dark ml-2">{produits.length}</span>
                </h3>
                <button
                  className="btn btn-outline-dark btn-sm"
                  onClick={handleAddProduit}
                >
                  <i className="fas fa-plus mr-1" /> Ajouter un produit
                </button>
              </div>
            </div>
            <div className="card-body p-0 table-responsive">
              <table className="table table-hover mb-0">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: 50 }} className="text-center">
                      #
                    </th>
                    <th>Désignation</th>
                    <th style={{ width: 160 }} className="text-center">
                      Quantité
                    </th>
                    <th style={{ width: 70 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {produits.map((p, i) => (
                    <tr key={i}>
                      <td className="align-middle text-center text-muted">
                        {i + 1}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ex: Ordinateur portable Dell XPS"
                          value={p.designation}
                          onChange={(e) =>
                            handleProduitChange(i, "designation", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control text-center"
                          min={1}
                          value={p.quantite}
                          onChange={(e) =>
                            handleProduitChange(i, "quantite", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-center align-middle">
                        {produits.length > 1 && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{ width: 36 }}
                            title="Retirer ce produit"
                            onClick={() => handleRemoveProduit(i)}
                          >
                            <i className="fas fa-trash" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-footer d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {produits.length} ligne{produits.length > 1 ? "s" : ""} · quantité
                totale <strong>{totalQuantite}</strong>
              </small>
              <button
                className="btn btn-link btn-sm text-dark"
                onClick={handleAddProduit}
              >
                <i className="fas fa-plus mr-1" /> Ajouter une ligne
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-end mb-4" style={{ gap: 10 }}>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate("/demandesAchat")}
            >
              Annuler
            </button>
            <button
              className="btn btn-dark"
              onClick={handleSubmit}
              disabled={loading}
            >
              <i
                className={`fas ${
                  loading ? "fa-spinner fa-spin" : "fa-paper-plane"
                } mr-1`}
              />
              {loading ? "Envoi en cours..." : "Soumettre la demande"}
            </button>
          </div>
        </div>
      </section>

      <Snackbar
        open={openErrorAlert}
        autoHideDuration={4000}
        onClose={() => setOpenErrorAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenErrorAlert(false)} severity="error">
          {errorAlertText}
        </Alert>
      </Snackbar>

      <Snackbar
        open={openSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setOpenSuccessAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setOpenSuccessAlert(false)} severity="success">
          Demande créée avec succès !
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AddDemandeAchat;
